import React, { useEffect, useState } from 'react';
import EnhancedTable from '../../components/table';
import Config from '../../config';
import { Button, Grid, Card, CardContent, Typography, ButtonBase } from '@mui/material';
import ContentLoader from '../../components/content_loader';
import { ResponsivePie } from '@nivo/pie';
import { Link } from 'react-router-dom';
import { getCompanyNames } from '../../helpers';
import axios from 'axios';

interface PaymentDashboardProps {
    setTitle?: (title: string) => void;
    isRetailer?: boolean;
    [key: string]: any;
}

interface PaymentBreakdownEntry {
    id: string;
    value: number;
    label: string;
}

interface PaymentSummary {
    Currency?: string;
    PendingShipment?: number;
    Pending?: number;
    Failed?: number;
    MostRecentPayout?: number | null;
    Breakdown?: Array<{ Partner: string; Amount: number }>;
    breakdown?: PaymentBreakdownEntry[];
}

const PaymentDashboard = (props: PaymentDashboardProps) => {
    const { setTitle, isRetailer } = props;
    const [summary, setSummary] = useState<PaymentSummary | undefined>(undefined);
    // `loaded` is derived from `summary` presence so the two can never diverge.
    // The original class set `{loaded: true, summary}` atomically; splitting that
    // into two hook setters allowed an intermediate render with loaded=true but
    // summary still undefined (no auto-batching in legacy render mode), which
    // crashed the guarded `summary.PendingShipment` reads below.
    const loaded = summary !== undefined;

    useEffect(() => {
        (async () => {
            if(setTitle) {
                setTitle('Payment Center');
            }
            let summary = (await axios.get(Config.api + '/odata/Company/Functions.GetPaymentSummary')).data;
            summary.breakdown = summary.Breakdown.map((r: any) => ({id: r.Partner, value: r.Amount, label: r.Partner}));
            setSummary(summary);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadAccounts = async () => {
        return (await axios.get(Config.api + '/api/v1/payment/partners')).data;
    };

    let currencyFormat = Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: ((summary || {}).Currency) || 'USD',
      minimumFractionDigits: 2
    }).format;
    let config = {
        getData: loadAccounts,
        columns: [
          { id: "Id", hidden: true},
          { id: 'CompanyName', filterable: true, sortable: true, label: 'Partner', width: '20em', getOptions: getCompanyNames },
          { id: 'ExternalAccountId', label: 'Account Number' },
          { id: 'ExternalWalletId', label: 'Balance Account Number' },
          { command: 'actions', template: (value: any, row: any) => row.ExternalAccountId || !isRetailer ? <Button variant='outlined' size='small' component={Link} to={`/payments/account?partner=${row.Id}`}>View</Button> : '', width: '10em'}
      ],
      order: 'asc',
      orderBy: 'CompanyName',
      keyField: 'Id',
      pageSize: 20,
      pageSizes: [10, 20, 50],
      title: 'Payment Accounts'
  };
    return <Grid container spacing={2}>
    <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3 }} style={{display: 'flex'}}>
    {loaded ?
            <ButtonBase component={Link} style={{display: 'flex', flex: 1, alignItems: 'unset'}} to='/payments/transfers?q.0=Status eq Pending Shipment'>
                <Card id="cuePendingShipmenttTransferCount" className="success" style={{flex: 1}}>
                    <CardContent>
                        <Typography variant="h6" component="h2">Transfers Pending Shipment</Typography>
                        <Typography variant="h4" component="h2" style={{fontWeight: '1000'}}>{Intl.NumberFormat('en-US').format(summary.PendingShipment || 0)}</Typography>
                    </CardContent>
                </Card>
            </ButtonBase>
        : <ContentLoader preserveAspectRatio='none' style={{height: '7em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>}
        </Grid>
        <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3 }} style={{display: 'flex'}}>
        {loaded ?
            <ButtonBase component={Link} style={{display: 'flex', flex: 1, alignItems: 'unset'}} to="/payments/transfers?q.0=Status eq Pending">
                <Card id="cuePendingTransferCount" className="success" style={{flex: 1}}>
                    <CardContent>
                        <Typography variant="h6" component="h2">Transfers Pending Payout</Typography>
                        <Typography variant="h4" component="h2" style={{fontWeight: '1000'}}>{Intl.NumberFormat('en-US').format(summary.Pending)}</Typography>
                    </CardContent>
                </Card>
            </ButtonBase>
        : <ContentLoader preserveAspectRatio='none' style={{height: '7em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>}
        </Grid>
        <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3 }} style={{display: 'flex'}}>
        {loaded ?
            <ButtonBase component={Link} style={{display: 'flex', flex: 1, alignItems: 'unset'}} to='/payments/transfers?q.0=Status eq Failed'>
                <Card id="cueFailedTransfers" className={summary.Failed ? 'failure' : 'success'} style={{flex: 1}}>
                    <CardContent>
                        <Typography variant="h6" component="h2">Failed Transfers</Typography>
                        <Typography variant="h4" component="h2" style={{fontWeight: '1000'}}>{Intl.NumberFormat('en-US').format(summary.Failed)}</Typography>
                    </CardContent>
                </Card>
            </ButtonBase>
        : <ContentLoader preserveAspectRatio='none' style={{height: '7em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>}
        </Grid>
        <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3 }} style={{display: 'flex'}}>
        {loaded ?
            <ButtonBase component={Link} style={{display: 'flex', flex: 1, alignItems: 'unset'}} to='/payments/transfers?q.0=PaymentType eq Payout'>
                <Card id="cueLatestPayout" className={'success'} style={{flex: 1}}>
                    <CardContent>
                        <Typography variant="h6" component="h2">Latest Payout</Typography>
                        <Typography variant="h4" component="h2" style={{fontWeight: '1000'}}>{summary.MostRecentPayout === null ? 'N/A' : currencyFormat(summary.MostRecentPayout)}</Typography>
                    </CardContent>
                </Card>
            </ButtonBase>
        : <ContentLoader preserveAspectRatio='none' style={{height: '7em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>}
        </Grid>
            <Grid size={12}>
                {loaded ? (summary.Breakdown ?
                    <Card>
                        <CardContent style={{height: '25em'}}>
                            <Typography className='chart-title'>{'All Pending Transfers'}</Typography>
                            <Typography className='chart-title'>{`Total: ${currencyFormat(summary.breakdown.reduce((acc, cur) => cur.value + acc, 0).toFixed(2) as any)}`}</Typography>
                            <ResponsivePie
                                {...({
                                    data: summary.breakdown,
                                    margin: {
                                        top: 60,
                                        right: 100,
                                        bottom: 100,
                                        left: 120
                                    },
                                    animate: true,
                                    padAngle: 1,
                                    enableSlicesLabels: false,
                                    tooltip: (d: PaymentBreakdownEntry) => `${d.id}: ${currencyFormat(d.value.toFixed(2) as any)}`,
                                    enableRadialLabels: true,
                                    innerRadius: 0.6
                                } as any)}
                            />
                        </CardContent>
                    </Card>
                    : <Card style={{height: '25em', justifyContent: 'center', display: 'flex', flexDirection: 'column'}}><div>There is no data to display.</div></Card>
                ) : <ContentLoader preserveAspectRatio='none' style={{height: '25em', width: '100%'}} primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>}
            </Grid>
            <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                <EnhancedTable config={config}/>
            </Grid>
            </Grid>;
};

export default PaymentDashboard;
